import React, { useState } from "react";
import { LuPencil, LuPlus, LuX } from "react-icons/lu";
import { Button, ConfirmDialog, SkeletonRows, Spinner } from "../../../components/ui";
import { cn } from "../../../components/ui/cn";
import { t } from "../../../i18n";

interface LinkedItem {
  id: number;
  name: string;
}

interface LinkedListProps<T extends LinkedItem> {
  items: T[];
  loading: boolean;
  loadingText: string;
  emptyText: string;
  unlinkTitle: string;
  subtitle?: (item: T) => string | null | undefined;
  /** Outra operação do hook em andamento: trava o Desvincular de todos. */
  busy: boolean;
  /** Chamado depois da confirmação; quem usa remove o item da lista se der certo. */
  onUnlink: (item: T) => Promise<void>;
  /** Com onEdit, cada item ganha o botão de editar (reservas). */
  onEdit?: (item: T) => void;
  /** Formulário de vincular, aberto pelo "+ Adicionar" embaixo da lista. */
  addForm: React.ReactNode;
}

/** Item das listas de Configurações (vínculos, e-mails, conta conectada). */
export const ITEM_CLASS = "flex items-center gap-3 rounded-control border border-line bg-surface py-3 pl-4 pr-3 transition-opacity duration-base";

/** Botão redondo de 40px dentro do item (editar, remover). */
export const ROW_ICON_BUTTON =
  "grid h-10 w-10 shrink-0 place-items-center rounded-full bg-surface-muted text-fg-muted transition-colors duration-fast disabled:cursor-not-allowed disabled:opacity-40";

/** "+ Adicionar" que abre o formulário embaixo de uma lista. */
export const AddLink: React.FC<{ onClick: () => void; children: React.ReactNode }> = ({ onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex min-h-11 items-center gap-2 self-start rounded-button pl-2 pr-3 text-body font-semibold text-primary transition-colors duration-fast hover:bg-primary-tint focus-visible:shadow-focus focus-visible:outline-none"
  >
    <span className="grid h-7 w-7 place-items-center rounded-full border-strong border-primary" aria-hidden>
      <LuPlus size={16} />
    </span>
    {children}
  </button>
);

// Vínculos do médico (empresas, hospitais, reservas): lista, Desvincular confirmado e "+ Adicionar".
export function LinkedList<T extends LinkedItem>({
  items, loading, loadingText, emptyText, unlinkTitle, subtitle, busy, onUnlink, onEdit, addForm,
}: LinkedListProps<T>) {
  const [adding, setAdding] = useState(false);
  const [toUnlink, setToUnlink] = useState<T | null>(null);
  const [unlinkingId, setUnlinkingId] = useState<number | null>(null);

  const handleConfirm = async () => {
    if (!toUnlink) return;
    const item = toUnlink;
    setToUnlink(null);
    setUnlinkingId(item.id);
    await onUnlink(item);
    setUnlinkingId(null);
  };

  const list = loading ? (
    <SkeletonRows label={loadingText} />
  ) : items.length === 0 ? (
    <p className="py-1 text-caption text-fg-subtle">{emptyText}</p>
  ) : (
    <ul className="flex flex-col gap-2.5">
      {items.map((item) => {
        const deleting = unlinkingId === item.id;
        const sub = subtitle?.(item);
        return (
          <li
            key={item.id}
            className={cn(ITEM_CLASS, deleting && "opacity-50")}
          >
            {deleting && <Spinner size={14} />}
            <span className="min-w-0 flex-1">
              <span className={cn("block break-words text-body", deleting ? "text-fg-subtle" : "text-fg")}>{item.name}</span>
              {sub && <span className="block break-words text-caption text-fg-subtle">{sub}</span>}
            </span>
            {onEdit && (
              <button
                type="button"
                onClick={() => onEdit(item)}
                disabled={deleting}
                aria-label={t("links.editLabel", { name: item.name })}
                className={cn(ROW_ICON_BUTTON, "hover:bg-primary-tint hover:text-primary")}
              >
                <LuPencil size={16} aria-hidden />
              </button>
            )}
            <button
              type="button"
              onClick={() => setToUnlink(item)}
              disabled={!!unlinkingId || busy}
              aria-label={t("links.unlinkLabel", { name: item.name })}
              className={cn(ROW_ICON_BUTTON, "hover:bg-danger-soft hover:text-danger")}
            >
              <LuX size={16} aria-hidden />
            </button>
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className="flex flex-col gap-3">
      {toUnlink && (
        <ConfirmDialog
          title={unlinkTitle}
          message={t("links.unlinkConfirm", { name: toUnlink.name })}
          confirmLabel={t("links.unlink")}
          onConfirm={handleConfirm}
          onCancel={() => setToUnlink(null)}
        />
      )}

      {list}

      {adding ? (
        <div className="flex flex-col gap-2 rounded-card border border-dashed border-primary-line bg-primary-tint p-3">
          {addForm}
          <Button variant="text" size="sm" className="self-end" onClick={() => setAdding(false)}>
            {t("common.cancel")}
          </Button>
        </div>
      ) : (
        <AddLink onClick={() => setAdding(true)}>{t("links.add")}</AddLink>
      )}
    </div>
  );
}
